<template>
  <b-modal
    ref="patientSelectionModal"
    :title="patientSelectionModalTitle"
    centered
    modal-class="patient-selection-modal"
  >
    <div
      class="mx-4"
      style="position:relative"
    >
      <div class="float-button d-flex align-items-center justify-content-center">
        <i
          class="fal fa-long-arrow-right"
          aria-hidden="true"
        />
      </div>
      <b-row>
        <b-col class="px-0 mb-2">
          <div class="alert-card d-flex align-items-center py-2">
            <div>
              <i
                class="fas fa-exclamation-triangle"
                aria-hidden="true"
              />
            </div>
            <div>
              <h3>{{ $t('component.patientSelectionModal.alertTitle') }}</h3>
              <span>{{ $t('component.patientSelectionModal.alertSubtitle') }}</span>
            </div>
          </div>
        </b-col>
      </b-row>
      <!-- Patient card header block -->
      <b-row>
        <b-col class="card-header-block">
          <div class="patient-header">
            <div class="text-center d-flex align-items-center justify-content-between my-2">
              <p class="patient-type">
                <i
                  class="main mr-2 fal fa-user-circle"
                  aria-hidden="true"
                />{{ $t('component.patientSelectionModal.externalPatient') }}
              </p>
              <div>
                <p>
                  <i
                    v-tooltip="$t('component.patientSelectionModal.lastModificationDate')"
                    class="mr-2 fal fa-calendar"
                    aria-hidden="true"
                  />{{ internalIntegrationPatient.originalConcurrencyUpdatedAt | datetime }}
                </p>
              </div>
            </div>
          </div>
        </b-col>
        <b-col
          cols="auto"
          class="px-0"
        >
          <div class="central-space">
            <div class="d-none link-line" />
          </div>
        </b-col>
        <b-col class="card-header-block selection">
          <div class="patient-header">
            <div class="text-center d-flex align-items-center justify-content-between my-2">
              <p class="patient-type">
                <i
                  class="main mr-2 fal fa-user-circle"
                  aria-hidden="true"
                />{{ $t('component.patientSelectionModal.localPatient') }}
              </p>
              <div>
                <p>
                  <i
                    v-tooltip="$t('component.patientSelectionModal.lastModificationDate')"
                    class="mr-2 fal fa-calendar"
                    aria-hidden="true"
                  />{{ internalLocalPatient.originalConcurrencyUpdatedAt | datetime }}
                </p>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
      <!-- Patient card data blocks -->
      <b-row>
        <patient-identifiers-card
          ref="integrationPatientIdentifiers"
          integration-patient
          :continue-with-case-creation="continueWithCaseCreation"
          :identifiers-diff="identifiersDiff"
          :patient="internalIntegrationPatient"
        />
        <b-col
          cols="auto"
          class="px-0"
        >
          <div class="central-space">
            <div class="d-none link-line" />
          </div>
        </b-col>
        <patient-identifiers-card
          ref="localPatientIdentifiers"
          :custom-class="['selection']"
          local-patient
          :identifiers-diff="identifiersDiff"
          :patient="internalLocalPatient"
        />
      </b-row>
      <b-row>
        <patient-personal-data-card
          ref="integrationPatientPersonalData"
          integration-patient
          :continue-with-case-creation="continueWithCaseCreation"
          :patient-data-diff="duplicatedPatientData"
          :patient="internalIntegrationPatient"
          :show-errors="showErrors"
        />
        <b-col
          cols="auto"
          class="px-0"
        >
          <div class="central-space">
            <div class="d-none link-line" />
          </div>
        </b-col>
        <patient-personal-data-card
          ref="localPatientPersonalData"
          :custom-class="['selection']"
          local-patient
          :patient-data-diff="duplicatedPatientData"
          :patient="internalLocalPatient"
        />
      </b-row>
      <b-row>
        <patient-address-info-card
          ref="integrationPatientAddressInfo"
          integration-patient
          :continue-with-case-creation="continueWithCaseCreation"
          :patient-data-diff="duplicatedPatientData"
          :patient="internalIntegrationPatient"
        />
        <b-col
          cols="auto"
          class="px-0"
        >
          <div class="central-space">
            <div class="d-none link-line" />
          </div>
        </b-col>
        <patient-address-info-card
          ref="localPatientAddressInfo"
          :custom-class="['selection']"
          local-patient
          :patient-data-diff="duplicatedPatientData"
          :patient="internalLocalPatient"
        />
      </b-row>
      <b-row>
        <patient-observations-card
          ref="integrationPatientObservations"
          integration-patient
          :continue-with-case-creation="continueWithCaseCreation"
          :patient-data-diff="duplicatedPatientData"
          :patient="internalIntegrationPatient"
        />
        <b-col
          cols="auto"
          class="px-0"
        >
          <div class="central-space">
            <div class="d-none link-line" />
          </div>
        </b-col>
        <patient-observations-card
          ref="localPatientObservations"
          :custom-class="['selection']"
          local-patient
          :patient-data-diff="duplicatedPatientData"
          :patient="internalLocalPatient"
        />
      </b-row>
      <!-- Patient card footer block -->
      <b-row>
        <b-col class="card-footer-block text-right pb-1" />
        <b-col
          cols="auto"
          class="px-0"
        >
          <div class="central-space">
            <div class="link-line d-none" />
          </div>
        </b-col>
        <b-col class="card-footer-block text-right pb-1 selection" />
      </b-row>
    </div>
    <div
      slot="modal-footer"
      class="footer-banner px-2"
      style="width:100%;"
    >
      <div class="row">
        <div class="col-5 text-left">
          <b-button
            size="sm"
            variant="outline-secondary"
            data-dismiss="modal"
            @click="hide"
          >
            {{ $t('global.cancel') }}
          </b-button>
        </div>
        <div class="col-7 text-right align-content-between">
          <b-button
            v-if="!continueWithCaseCreation"
            qa="patient-selection-modal-create-and-continue-button"
            size="sm"
            class="btn__outline mr-2"
            @click="createAndContinue"
          >
            <i
              class="fal fa-user-plus mr-2"
              aria-hidden="true"
            />
            {{ $t('component.patientSelectionModal.createAndContinue') }}
          </b-button>
          <b-button
            qa="patient-selection-modal-update-and-continue-button"
            size="sm"
            class="btn__outline mr-2"
            @click="updateLocalPatientAndContinue"
          >
            <i
              class="fal fa-save mr-2"
              aria-hidden="true"
            />
            {{ $t('component.patientSelectionModal.updateLocalPatientAndContinue') }}
          </b-button>
          <b-button
            qa="patient-selection-modal-continue-with-local-button"
            size="sm"
            class="btn--primary"
            @click="continueWithLocalPatient"
          >
            <i
              class="fal fa-check mr-2"
              aria-hidden="true"
            />
            {{ $t('component.patientSelectionModal.continueWithLocalPatient') }}
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import PatientIdentifiersCard from '@/modules/RegistryModule/components/patientDiscrepancyModal/PatientIdentifiersCard'
import PatientAddressInfoCard from '@/modules/RegistryModule/components/patientDiscrepancyModal/PatientAddressInfoCard'
import PatientPersonalDataCard from '@/modules/RegistryModule/components/patientDiscrepancyModal/PatientPersonalDataCard'
import PatientObservationsCard
    from '@/modules/RegistryModule/components/patientDiscrepancyModal/PatientObservationsCard'
import patientDiscrepancyServices from '@/api/resources/patientDiscrepancyServices'
import { isPlainObject, isEmpty, differenceBy } from 'lodash'
export default {
    name: 'PatientSelectionModal',
    components: { PatientObservationsCard, PatientAddressInfoCard, PatientIdentifiersCard, PatientPersonalDataCard },
    props: {
        continueWithCaseCreation: { type: Boolean, default: false }
    },
    data() {
        return {
            internalLocalPatient: {},
            internalIntegrationPatient: {},
            internalTemporalPatientId: null,
            requestId: null,
            requestLabel: '',
            laboratoryId: null,
            identifiersDiff: [],
            duplicatedPatientData: {},
            showErrors: false
        }
    },
    computed: {
        patientSelectionModalTitle() {
            const title = this.$t('component.patientSelectionModal.patientDiscrepancy')
            return this.continueWithCaseCreation ? title : `${title}: ${this.requestLabel}`
        }
    },
    methods: {
        show(localPatient, integrationPatient, temporalPatientId, requestId, requestLabel, laboratoryId) {
            this.requestId = requestId
            this.laboratoryId = laboratoryId
            this.internalLocalPatient = {
                ...localPatient,
                address: this.mapPatientAddress(localPatient)
            }
            this.internalIntegrationPatient = {
                ...integrationPatient,
                address: this.mapPatientAddress(integrationPatient)
            }
            this.internalTemporalPatientId = temporalPatientId
            this.requestLabel = requestLabel
            this.setDataWithConflict(this.internalIntegrationPatient, this.internalLocalPatient)
            this.$refs.patientSelectionModal.show()
        },
        mapPatientAddressLocation(patientAddressLocation) {
            return {
                active: patientAddressLocation.active,
                code: patientAddressLocation.code,
                id: patientAddressLocation.id,
                name: patientAddressLocation.name
            }
        },
        mapPatientAddress(localPatient) {
            const address = localPatient.address
            return {
                address: address.address,
                country: address.country ?? {},
                province: address.province ? this.mapPatientAddressLocation(address.province) : {},
                municipality: address.municipality ? this.mapPatientAddressLocation(address.municipality) : {},
                postalCode: address.postalCode
            }
        },
        getIdentifiersDiff(integrationPatientIdentifiers, localPatientIdentifiers) {
            return differenceBy(integrationPatientIdentifiers, localPatientIdentifiers, 'paiIdentifier')
        },
        getDuplicatedData(integrationPatientData, localPatientData) {
            let duplicatedData = {}
            if (isPlainObject(integrationPatientData)) {
                for (const patientData in integrationPatientData) {
                    if (isPlainObject(integrationPatientData[patientData])) {
                        duplicatedData[patientData] = this.getDuplicatedData(integrationPatientData[patientData], localPatientData[patientData])
                    } else if ((integrationPatientData[patientData] !== localPatientData[patientData])) {
                        duplicatedData[patientData] = integrationPatientData[patientData]
                    }
                }
            } else {
                if (integrationPatientData !== localPatientData) {
                    duplicatedData = integrationPatientData
                }
            }
            return duplicatedData
        },
        getDuplicatedPatientData(integrationPatient, localPatient) {
            let duplicatedData = {}
            for (const patientData in integrationPatient) {
                if (patientData === 'patientIdentifiers') {
                    this.identifiersDiff = this.getIdentifiersDiff(integrationPatient[patientData], localPatient[patientData])
                } else {
                    duplicatedData[patientData] = this.getDuplicatedData(integrationPatient[patientData], localPatient[patientData])
                }
            }
            return duplicatedData
        },
        setDataWithConflict(integrationPatient, localPatient) {
            this.duplicatedPatientData = this.getDuplicatedPatientData(integrationPatient, localPatient)
        },
        mapFieldsToRequest() {
            const selectedIdentifiers = this.$refs.integrationPatientIdentifiers.identifiers.filter(identifier => identifier.selected)
            return {
                address: {
                    address: this.$refs.integrationPatientAddressInfo.address.address.selected
                        ? this.$refs.integrationPatientAddressInfo.address.address.value : undefined,
                    postalCode: this.$refs.integrationPatientAddressInfo.address.postalCode.selected
                        ? this.$refs.integrationPatientAddressInfo.address.postalCode.value : undefined,
                    municipalityCode: this.$refs.integrationPatientAddressInfo.address.municipality.selected
                        ? this.$refs.integrationPatientAddressInfo.address.municipality.value : undefined,
                    provinceCode: this.$refs.integrationPatientAddressInfo.address.province.selected
                        ? this.$refs.integrationPatientAddressInfo.address.province.value : undefined,
                    countryCode: this.$refs.integrationPatientAddressInfo.address.country.selected
                        ? this.$refs.integrationPatientAddressInfo.address.country.value : undefined
                },
                birthDate: this.$refs.integrationPatientPersonalData.birthdate.selected
                    ? this.$refs.integrationPatientPersonalData.birthdate.value : undefined,
                contactInformation: {
                    email: this.$refs.integrationPatientAddressInfo.contactInformation.email.selected
                        ? this.$refs.integrationPatientAddressInfo.contactInformation.email.value : undefined,
                    mainPhone: this.$refs.integrationPatientAddressInfo.contactInformation.mainPhone.selected
                        ? this.$refs.integrationPatientAddressInfo.contactInformation.mainPhone.value : undefined,
                    otherPhone1: this.$refs.integrationPatientAddressInfo.contactInformation.otherPhone1.selected
                        ? this.$refs.integrationPatientAddressInfo.contactInformation.otherPhone1.value : undefined,
                    otherPhone2: this.$refs.integrationPatientAddressInfo.contactInformation.otherPhone2.selected
                        ? this.$refs.integrationPatientAddressInfo.contactInformation.otherPhone2.value : undefined
                },
                notes: this.$refs.integrationPatientObservations.observations.selected
                    ? this.$refs.integrationPatientObservations.observations.value : undefined,
                gender: this.$refs.integrationPatientPersonalData.gender.selected
                    ? this.mapGender(this.$refs.integrationPatientPersonalData.gender.code) : undefined,
                identifiers: this.mapIdentifiers(selectedIdentifiers),
                name: this.$refs.integrationPatientPersonalData.name.selected
                    ? this.$refs.integrationPatientPersonalData.name.value : undefined,
                surname1: this.$refs.integrationPatientPersonalData.surname1.selected
                    ? this.$refs.integrationPatientPersonalData.surname1.value : undefined,
                surname2: this.$refs.integrationPatientPersonalData.surname2.selected
                    ? this.$refs.integrationPatientPersonalData.surname2.value : undefined
            }
        },
        async patchLocalPatientPartiallyAndDeleteTemporalPatient(requestWithoutFalsyValues) {
            this.updateLoading(true)
            const [error] = await this.toCall(patientDiscrepancyServices.patchLocalPatientPartiallyAndDeleteTemporalPatient(this.laboratoryId, this.requestId, this.internalLocalPatient.patId, requestWithoutFalsyValues))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return false
            }
        },
        async updateLocalPatientPartiallyWithHisPatientInformation(requestWithoutFalsyValues) {
            this.updateLoading(true)
            const [error] = await this.toCall(patientDiscrepancyServices.updateLocalPatientPartiallyWithHisPatientInformation(this.laboratoryId, this.internalLocalPatient.patId, requestWithoutFalsyValues))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return false
            }
            return true
        },
        async updateLocalPatientAndContinue() {
            // Empty fields won't be sent
            const request = this.mapFieldsToRequest()
            let patientToContinueWith = null
            let requestWithoutFalsyValues = {}
            for (const requestData in request) {
                if (!isEmpty(this.getRequestWithoutFalsyValues(request[requestData]))) {
                    requestWithoutFalsyValues[requestData] = this.getRequestWithoutFalsyValues(request[requestData])
                }
            }
            if (!this.continueWithCaseCreation) {
                await this.patchLocalPatientPartiallyAndDeleteTemporalPatient(requestWithoutFalsyValues)
                this.$emit('continue')
            } else {
                const updateLocalPatientPartiallyWithHisPatientInformationResponse = await this.updateLocalPatientPartiallyWithHisPatientInformation(requestWithoutFalsyValues)
                if (updateLocalPatientPartiallyWithHisPatientInformationResponse) {
                    patientToContinueWith = this.internalLocalPatient
                    this.$emit('updateLocalPatientAndContinueWithCaseCreation', patientToContinueWith)
                }
            }
            this.hide()
        },
        mapIdentifiers(identifiers) {
            let mappedIdentifiers = []
            identifiers.forEach(identifier => {
                const identifierValue = identifier.value
                let mappedIdentifier = {
                    identifier: identifierValue.paiIdentifier,
                    identifierType: identifierValue.identifierType.code
                }
                if (identifierValue.center) {
                    mappedIdentifier.centerCode = identifierValue.center.code
                }
                mappedIdentifiers.push(mappedIdentifier)
            })
            return mappedIdentifiers
        },
        mapGender(genderCode) {
            const genderCodes = {
                F: 'gender.female.code',
                M: 'gender.male.code'
            }
            return genderCodes[genderCode]
        },
        getRequestWithoutFalsyValues(requestData) {
            let requestWithoutFalsyValues = {}
            if (isPlainObject(requestData)) {
                for (const prop in requestData) {
                    if (isPlainObject(requestData[prop])) {
                        requestWithoutFalsyValues[prop] = this.getRequestWithoutFalsyValues(requestData[prop])
                    } else {
                        requestWithoutFalsyValues[prop] = requestData[prop]
                    }
                }
            } else {
                requestWithoutFalsyValues = requestData
            }
            return requestWithoutFalsyValues
        },
        validate() {
            return this.$refs.integrationPatientPersonalData.gender && this.$refs.integrationPatientPersonalData.name && this.$refs.integrationPatientPersonalData.surname1
        },
        async createAndContinue() {
            if (!this.validate()) {
                this.showErrorModalGlobal(this.$t('studyRequest.create.incompleteFormModal.content'))
                this.showErrors = true
            } else {
                this.showErrors = false
                // Empty fields won't be sent
                const request = this.mapFieldsToRequest()
                let requestWithoutFalsyValues = {}
                for (const requestData in request) {
                    if (!isEmpty(this.getRequestWithoutFalsyValues(request[requestData]))) {
                        requestWithoutFalsyValues[requestData] = this.getRequestWithoutFalsyValues(request[requestData])
                    }
                }
                this.updateLoading(true)
                const [error] = await this.toCall(patientDiscrepancyServices.patchTemporalPatientPartiallyAndDeleteLocalPatient(this.laboratoryId, this.requestId, this.internalTemporalPatientId, requestWithoutFalsyValues))
                this.updateLoading(false)
                if (error) {
                    this.showErrorModalGlobalFromErrorResponse(error)
                    return
                }
                this.hide()
                this.$emit('continue')
            }
        },
        async continueWithLocalPatient() {
            let patientToContinueWith = null
            if (!this.continueWithCaseCreation) {
                this.updateLoading(true)
                const [error] = await this.toCall(patientDiscrepancyServices.keepLocalPatientWithoutUpdating(this.laboratoryId, this.requestId))
                this.updateLoading(false)
                if (error) {
                    this.showErrorModalGlobalFromErrorResponse(error)
                    return
                }
            } else {
                patientToContinueWith = this.internalLocalPatient
            }
            this.$emit('continue', patientToContinueWith)
            this.hide()
        },
        hide() {
            this.$refs.patientSelectionModal.hide()
        }
    }
}
</script>

<style lang="scss">
    .patient-selection-modal .float-button {
        background: vt-color(white-light);
        color: vt-color(white-light);
        position: fixed;
        z-index: 1;
        width: 40px;
        height: 40px;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        margin: auto;
        box-shadow: 1px 1px 5px 0px rgba(125, 125, 125, 0.64);
        border-radius: 50%;
        transition: .3s;
    }
    .patient-selection-modal .float-button i {
        font-size: 20px;
        color: vt-color(success);
    }
    .patient-selection-modal .item-selected {
        color: vt-color(success);
    }
    .patient-selection-modal .badge {
        text-transform: uppercase;
    }
    .patient-selection-modal .badge.primary {
        background: vt-color(primary);
    }
    .patient-selection-modal .badge.alt {
        background: vt-color(success);
    }
    .patient-selection-modal .alert-card {
        border: 1px solid vt-color(grid-general-border);
        border-radius: 3px;
        border-left: 3px solid vt-color(red-high);
        background: vt-color(red-medium, .9);
    }
    .patient-selection-modal .alert-card i {
        color: vt-color(red-high);
        font-size: font-size(very-big);
        margin: 0 15px;
    }
    .patient-selection-modal .alert-card h3 {
        margin-bottom: 0;
        font-size: font-size(normal);
        font-weight: bold;
    }
    .patient-selection-modal .alert-card span {
        font-size: font-size(very-small);
        color: vt-color(dark-text);
    }
    .patient-selection-modal .link-line {
        position: absolute;
        width: 100%;
        height: 4px;
        background: vt-color(warning);
        margin-top: -1rem;
    }
    .patient-selection-modal .central-space {
        width: 15px;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
    }
    .patient-selection-modal .card-header-block {
        border-top: 1px solid #cacaca;
        border-left: 1px solid #cacaca;
        border-right: 1px solid #cacaca;
        border-radius: 3px 3px 0 0;
    }
    .patient-selection-modal .card-data-block {
        border-left: 1px solid #cacaca;
        border-right: 1px solid #cacaca;
    }
    .patient-selection-modal .card-footer-block {
        border-bottom: 1px solid #cacaca;
        border-left: 1px solid #cacaca;
        border-right: 1px solid #cacaca;
        border-radius: 0 0 3px 3px;
    }
    .patient-selection-modal .card-header-block.selection {
        border-top: 2px solid vt-color(success);
        border-left: 2px solid vt-color(success);
        border-right: 2px solid vt-color(success);
        border-radius: 3px 3px 0 0;
    }
    .patient-selection-modal .card-data-block.selection {
        border-left: 2px solid vt-color(success);
        border-right: 2px solid vt-color(success);
    }
    .patient-selection-modal .card-footer-block.selection {
        border-bottom: 2px solid vt-color(success);
        border-left: 2px solid vt-color(success);
        border-right: 2px solid vt-color(success);
        border-radius: 0 0 3px 3px;
    }
    .patient-selection-modal .patient-selection-card .structure {
        position: relative;
    }
    .patient-selection-modal .card-data-block .structure .main {
        font-size: font-size(very-small);
    }
    .line-left {
        width: 30px;
        height: 2px;
        background: vt-color(warning);
        position: absolute;
        top: 50%;
        right: -30px;
    }
    .patient-selection-modal .patient-selection-card .card-body {
        padding: 0 10px;
    }
    .patient-selection-modal {
        .highlight {
            margin-bottom: 0.5rem;
            h4,
            div:not(.custom-checkbox) {
                color: vt-color(red-high);
                background: vt-color(red-high, .95);
                padding: 0 0.35rem;
                border-left: 2px solid vt-color(red-high);
            }
        }
    }
    .patient-selection-modal {
        .highlight {
            h4 {
                padding-bottom: 0.25rem;
            }
        }
    }
    .patient-selection-modal .structure.section-highlight {
        border-color: vt-color(red-medium, .3);
    }
    .patient-selection-modal .patient-header {
        .patient-type {
            font-weight: bold;
        }
        p {
            margin-bottom: 0;
            margin-top: 5px;
            color: vt-color(dark-text);
        }
    }
    .patient-selection-modal .top-item span {
        font-size: font-size(small);
        color: vt-color(dark-text);
    }
    .patient-selection-modal .top-item {
        line-height: 13px;
    }
    .patient-selection-modal .modal-dialog {
        max-width: 75% !important;
    }
    .patient-selection-modal .modal-body {
        border-radius: .3rem;
    }
    .patient-selection-modal h4.item-selected {
        color: blue;
    }
    @media (max-width: 1300px) {
        .patient-selection-modal .modal-dialog {
            max-width: 95% !important;
        }
    }
</style>
